import { ACTION_TYPES, READ_SOURCES } from '../journey/action-types.ts'
import type { ActionType } from '../journey/execute/action.ts'
import { CHECK_ASSERTIONS, countsInstances, type JourneyCheck } from '../journey/check-types.ts'
import { describeMiss, flatTargetCandidates, flatTargetDefinitions, nonIdentityKeyStale, resolveDefinition, viewMatch } from '../stable-surfaces/resolve-definition.ts'
import { COMPONENT_TARGET, durableTarget, SIGHTMAP_TARGET, type ComponentTarget } from '../stable-surfaces/target.ts'
import { z } from 'zod'
import { flattenComponents, selectorsOf, type SightmapComponent, type SightmapDoc } from '../polish/sightmap/schema.ts'
import { normalizeRecordedSelector } from '../journey/selector-normalize.ts'
import { legacyReadLocator } from '../step-action/legacy-locator.ts'
import { legacyReadStepTarget, namesElement, sightmapAddress, type LegacyStepTarget, type LegacyStepTargetFields } from '../step-action/legacy-read.ts'
import type { DefinitionId, SurfaceId } from '../stable-surfaces/id.ts'

/** A stored step guard (legacy-check.ts reads it). */
export type JourneyCondition =
  | { type: 'route'; operator: 'matches' | 'not_matches'; pattern: string }
  | { type: 'selector'; operator: 'exists' | 'not_exists'; selector: string }
  | { type: 'component'; operator: 'exists' | 'not_exists'; component: ComponentTarget }
  | { type: 'all' | 'any'; conditions: JourneyCondition[] }
  | { type: 'not'; condition: JourneyCondition }
// Persisted schemas still read selector conditions so conversion can bind historical evidence without dropping gates.
export const JOURNEY_CONDITION: z.ZodType<JourneyCondition, z.ZodTypeDef, unknown> = z.lazy(() => z.union([
  z.object({ type: z.literal('route'), operator: z.enum(['matches', 'not_matches']), pattern: z.string() }),
  z.object({ type: z.literal('component'), operator: z.enum(['exists', 'not_exists']), component: COMPONENT_TARGET }),
  z.object({ type: z.literal('selector'), operator: z.enum(['exists', 'not_exists']), selector: z.string() }),
  z.object({ type: z.enum(['all', 'any']), conditions: z.array(JOURNEY_CONDITION) }),
  z.object({ type: z.literal('not'), condition: JOURNEY_CONDITION }),
]))
// Fresh conditions share the same complete targets as actions and checks.
export const AUTHORED_CONDITION: z.ZodType<JourneyCondition, z.ZodTypeDef, unknown> = z.lazy(() => z.union([
  z.object({ type: z.literal('route'), operator: z.enum(['matches', 'not_matches']), pattern: z.string() }).strict(),
  z.object({ type: z.literal('component'), operator: z.enum(['exists', 'not_exists']), component: SIGHTMAP_TARGET }).strict(),
  z.object({ type: z.enum(['all', 'any']), conditions: z.array(AUTHORED_CONDITION).min(1) }).strict(),
  z.object({ type: z.literal('not'), condition: AUTHORED_CONDITION }).strict(),
]))
// TargetRule: the one statement of which steps and checks need a target, and what identifies one.
/** What an action acts on. `required`: a named target only. `focus`: a named target, else the focused element.
 * `source`: a named element, else its own non-element source. `none`: no element. */
type TargetRule = 'required' | 'focus' | 'source' | 'none'
/** Who asks. `decision`: an act a model decided for this page (sequence, recovery, abstract resolution).
 *  `persisted`: a stored step, or one about to be stored. */
export type TargetContext = 'decision' | 'persisted'
const both = <R extends TargetRule>(rule: R) => ({ decision: rule, persisted: rule })
const TARGET_RULES = {
  click: both('required'),
  hover: both('required'),
  mousemove: both('required'),
  select: both('required'),
  drag: both('required'),
  dragTo: both('required'),
  uploadFile: both('required'),
  dropFile: both('required'),
  scrollIntoView: both('required'),
  type: { decision: 'focus', persisted: 'required' },
  input: { decision: 'focus', persisted: 'required' },
  pressKey: both('focus'),
  read_variable: both('source'),
  navigate: both('none'),
  back: both('none'),
  scrollToBottom: both('none'),
  scrollToTop: both('none'),
  scrollDown: both('none'),
  scrollUp: both('none'),
  awaitEmail: both('none'),
  abstract: both('none'),
  abstractActionSequence: both('none'),
  callTool: both('none'),
  reachSurface: both('none'),
  checkpoint: both('none'),
} as const satisfies Record<ActionType, Record<TargetContext, TargetRule>>
/** Action types a decided act must name a target for: the executor refuses them without one. */
export type TargetedActionType = { [T in ActionType]: (typeof TARGET_RULES)[T]['decision'] extends 'required' ? T : never }[ActionType]
export function targetRule(type: string, context: TargetContext): TargetRule {
  return Object.prototype.hasOwnProperty.call(TARGET_RULES, type) ? TARGET_RULES[type as ActionType][context] : 'none'
}
export function requiresTarget(type: string, context: TargetContext): boolean {
  return targetRule(type, context) === 'required'
}
const typesWith = (rule: TargetRule, context: TargetContext): ActionType[] => ACTION_TYPES.filter(type => TARGET_RULES[type][context] === rule)
// A rule's predicate for one listed type, and for several.
const RULE_PREDICATES = {
  required: ['acts on one element and needs its target', 'act on one element and need its target'],
  focus: ['acts on a target when it names one, else on the focused element', 'act on a target when they name one, else on the focused element'],
  source: [`reads a target when it names one, else its readFrom source (${READ_SOURCES.join(', ')}) or its value`,
    `read a target when they name one, else their readFrom source (${READ_SOURCES.join(', ')}) or their value`],
} as const satisfies Record<Exclude<TargetRule, 'none'>, readonly [string, string]>
/** The TargetRule for actions in `context`, as prompts state it. */
export function actionTargetRule(context: TargetContext): string {
  const sentence = (rule: keyof typeof RULE_PREDICATES) => {
    const types = typesWith(rule, context)
    return `${types.join(', ')} ${RULE_PREDICATES[rule][types.length === 1 ? 0 : 1]}.`
  }
  return [sentence('required'), sentence('focus'), sentence('source'), 'Other actions take no target.'].join(' ')
}
const UNTARGETED_CHECKS = new Set<string>(['abstract', 'reachSurfaceCheck', 'url_contains'])
const ELEMENT_CHECK_ASSERTIONS = CHECK_ASSERTIONS.filter(assertion => !UNTARGETED_CHECKS.has(assertion))
const TARGET_FIX = 'set "label" to a discovered interaction label, or "component" to the observed Sightmap hierarchy {surfaceId, path}; when neither identifies the element, make it "abstract"'
export const TARGET_RULE = [
  actionTargetRule('persisted'),
  `Checks with assertion ${ELEMENT_CHECK_ASSERTIONS.join(', ')} need a Sightmap target.`,
  'A target is "label", a discovered interaction label, or "component", the observed Sightmap hierarchy {surfaceId, path}. A check label must be an interaction label listed with a Sightmap target.',
  'No other field identifies an element: no raw selector, no text matching of the action description. When neither identifies the element, make the step or check "abstract".',
].join(' ')
/** Whether an action type may name a target. */
export function takesTarget(type: string, context: TargetContext): boolean {
  return targetRule(type, context) !== 'none'
}
interface TargetedAction {
  type: string; action: string; condition?: JourneyCondition; keyboardEvent?: string; selector?: string; label?: string; surfaceId?: SurfaceId; controlId?: DefinitionId; controlName?: string; component?: ComponentTarget; destination?: ComponentTarget; value?: string; inRunAnchors?: unknown
}
/** Whether the action names a Sightmap identity: a component path, or a recorded control on a known Surface. */
export function carriesComponentIdentity(action: LegacyStepTargetFields): boolean {
  return !!sightmapAddress(legacyReadStepTarget(action))
}
/** Whether an action acts on one element: its type always does, or it is optionally targeted and names one (a keyup acts on none). */
export function needsComponent(action: TargetedAction, context: TargetContext): boolean {
  const rule = targetRule(action.type, context)
  if (rule === 'required') return true
  return rule !== 'none' && !(action.type === 'pressKey' && action.keyboardEvent === 'keyup') && namesElement(legacyReadStepTarget(action))
}
/** Whether an action needs a target in `context` and names no element. */
export function lacksTarget(action: TargetedAction, context: TargetContext): boolean {
  return requiresTarget(action.type, context) && !namesElement(legacyReadStepTarget(action))
}
/** The authoring problem of an action that needs a target and carries no target identity. */
export function missingActionTarget(action: TargetedAction): string | null {
  return needsComponent(action, 'persisted') && !carriesComponentIdentity(action) ? `action "${action.action}" (${action.type}) has no target: ${TARGET_FIX}` : null
}

/** The authoring problem of an element check without a Sightmap target. */
export function missingCheckTarget(check: JourneyCheck): string | null {
  if (UNTARGETED_CHECKS.has(check.assertion) || SIGHTMAP_TARGET.safeParse(check.component).success) return null
  const label = check.label ? `: label ${check.label} has no observed Sightmap component` : ''
  return `check "${check.description}" (assertion ${check.assertion}) has no Sightmap target${label}; ${TARGET_FIX}`
}

/** `action` without its Sightmap and Surface identity fields (component, control, Surface) and the observation-local
 *  label; its selector stays. */
export function withoutSightmapIdentity<T extends TargetedAction>(action: T): Omit<T, 'component' | 'surfaceId' | 'controlId' | 'controlName' | 'label'> {
  const { component: _component, surfaceId: _surfaceId, controlId: _controlId, controlName: _controlName, label: _label, ...rest } = action
  return rest
}

/** Convert an interaction the Sightmap cannot identify into a live-resolved abstract step, keeping its
 * recorded selector as the author-time witness. An abstract step never executes that selector — the
 * live resolver owns control choice — but the runner's authored-target guard reads it to reject a
 * substitute control on any page the recorded control never lived on, so dropping it would leave the
 * resolver free to click a lookalike on an unrelated surface. Strip only the identity evidence the map
 * could not verify. */
export function resolveAsAbstract<T extends TargetedAction>(action: T): T {
  return { ...withoutSightmapIdentity(action), type: 'abstract' } as T
}

/** Convert authoring evidence into an identity, never persist an unbound selector as an interaction. */
export function bindToolComponent<T extends TargetedAction>(action: T, sightmap?: SightmapDoc): T {
  const bound = bindActionComponent(action, sightmap)
  if (!action.condition) return bound
  const bind = (condition: JourneyCondition): JourneyCondition => {
    if (condition.type === 'route') return condition
    if (condition.type === 'not') return { ...condition, condition: bind(condition.condition) }
    if ('conditions' in condition) return { ...condition, conditions: condition.conditions.map(bind) }
    const component = condition.type === 'component'
      ? storedReference(condition.component, sightmap, countsInstances(condition.operator))
      : condition.selector === action.selector && bound.component?.path
        ? bound.component
        : bindActionComponent<TargetedAction>({ type: 'click', action: 'Bind condition target', selector: condition.selector }, sightmap).component
    if (!component) throw new Error('Condition requires a Sightmap target')
    return AUTHORED_CONDITION.parse({ type: 'component', operator: condition.operator, component: durableTarget(component) })
  }
  return { ...bound, condition: bind(action.condition) }
}

/** The form a binding persists: the map's canonical reference. A target the map cannot resolve, or that carries a
 *  key resolution stales for its role, fails the binding. */
// [stable-surfaces]
function storedReference(target: ComponentTarget, sightmap: SightmapDoc | undefined, collection: boolean): ComponentTarget {
  if (!sightmap) return target
  const resolution = resolveDefinition(target, sightmap)
  if (resolution.kind !== 'unique') throw new Error(describeMiss(resolution))
  const keyStale = nonIdentityKeyStale(resolution.hit, collection)
  if (keyStale) throw new Error(describeMiss(keyStale))
  return resolution.hit.reference
}

function bindActionComponent<T extends TargetedAction>(action: T, sightmap?: SightmapDoc): T {
  // In-run anchors are facts about one run's observation, never shared identity.
  const { inRunAnchors: _inRunAnchors, ...shared } = action
  action = shared as T
  if (action.component) action = { ...action, component: durableTarget(action.component) }
  if ('checks' in action && Array.isArray(action.checks)) {
    action = { ...action, checks: action.checks.map(check => {
      if (check && typeof check === 'object' && 'component' in check && check.component) {
        const { selector: _selector, label: _label, ...rest } = check
        return { ...rest, component: durableTarget(storedReference(COMPONENT_TARGET.parse(check.component), sightmap, countsInstances(check.assertion))) }
      }
      return check
    }) }
  }
  if (action.destination) {
    const { value: _value, ...rest } = action
    action = { ...rest, destination: durableTarget(storedReference(action.destination, sightmap, false)) } as T
  }
  if (!needsComponent(action, 'persisted')) return action
  if (action.component) action = { ...action, component: storedReference(action.component, sightmap, false) }
  if (action.component?.path) {
    const { selector: _selector, label: _label, controlId: _controlId, ...rest } = action
    return { ...rest, component: durableTarget(action.component), surfaceId: action.component.surfaceId } as T
  }
  const target = legacyReadStepTarget(action)
  if (!sightmap) {
    const address = sightmapAddress(target)
    if (!address) throw new Error(missingActionTarget(action)!)
    return { ...action, component: action.component ?? address, surfaceId: address.surfaceId, controlId: address.controlId }
  }
  const targets = flatTargets(target, sightmap)
  const resolved = flatTargetDefinitions(sightmap, targets)
  if (resolved.length !== 1) {
    const single = targets.length === 1 ? resolveDefinition(targets[0]!, sightmap) : undefined
    throw new Error(`Cannot author tool action "${action.action}": ${single?.kind === 'stale' ? describeMiss(single) : `target must identify exactly one Sightmap component (found ${resolved.length}).`}`)
  }
  const { reference } = resolved[0]!
  const { selector: _selector, label: _label, ...rest } = action
  return { ...rest, surfaceId: reference.surfaceId, component: reference } as T
}

/** Flat targets a target without a component path names: its recorded control, or the view components whose selector
 *  is its literal locator, both in the read (normalized) form. A locator with variables names no one component. */
function flatTargets(target: LegacyStepTarget, sightmap: SightmapDoc): ComponentTarget[] {
  const { surfaceId, control, selector } = target
  if (control) return flatTargetCandidates(sightmap, control.controlId, surfaceId)
  const locator = selector ? legacyReadLocator(selector) : null
  if (!locator || !('raw' in locator)) return []
  const names = (component: SightmapComponent) => selectorsOf(component).some(selector => normalizeRecordedSelector(selector) === locator.raw)
  return sightmap.views.filter(view => !surfaceId || viewMatch(view, surfaceId)).flatMap(view => flattenComponents(view.components)
    .flatMap(component => view.id && component.id && names(component) ? [{ surfaceId: view.id, controlId: component.id }] : []))
}

/** Fresh element checks must already name the observed Sightmap instance. Historical catalogs
 * remain readable; this boundary applies before new authoring is judged or persisted. */
export function bindAuthoredCheck<T extends string | JourneyCheck>(check: T): T {
  if (typeof check === 'string' || UNTARGETED_CHECKS.has(check.assertion)) return check
  const structured: JourneyCheck = check
  const problem = missingCheckTarget(structured)
  if (problem) throw new Error(problem)
  const { selector: _selector, label: _label, surfaceId: _surfaceId, controlId: _controlId, ...rest } = structured
  return { ...rest, component: SIGHTMAP_TARGET.parse(structured.component) } as T
}

/** The authoring boundary for one new action: an authored condition and Sightmap-bound checks. */
export function bindAuthoredAction<T extends { condition?: unknown; checks: Array<string | JourneyCheck> }>(action: T): T {
  return { ...action, ...(action.condition ? { condition: AUTHORED_CONDITION.parse(action.condition) } : {}), checks: action.checks.map(bindAuthoredCheck) }
}
